import React, { useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../../../components/core/PageHeader/PageHeader';
import * as monitoredCounterApi from '../../../api/app/monitoredCounterApi';
import * as monitoredCycleTimeApi from '../../../api/app/monitoredCycleTimeApi';
import * as monitoredStateApi from '../../../api/app/monitoredStateApi';
import * as assetApi from '../../../api/app/assetApi';
import { useQuery } from 'react-query';
import { Loader } from '../../../components/core/Loader/Loader';
import { MonitoredCounter } from '../../../types/app/monitoredCounter.types';
import { MonitoredCycleTime } from '../../../types/app/monitoredCycleTime.types';
import { Asset } from '../../../types/app/asset.types';
import { useParams } from 'react-router-dom';
import { MonitoredState } from '../../../types/app/monitoredState.types';
import { Timezone } from './DashboardTimezoneModal';
import { pathFullscreen, paths } from '../../../utils/core/routes';
import { Box, Button, Tooltip, Modal, Grid } from '@mui/material';
import { useStyles } from './Dashboard.styles';
import { DashboardDataLoader } from './DashboardDataLoader';
import { TimezoneChanger } from '../../../components/app/TimezoneChanger/TimezoneChanger';
import { useTimezoneDialog } from '../../../hooks/useTimezoneDialog';
import { configNoCache } from '../../../api/core/api';
import { useTranslation } from '../../../contexts/translation';
import { Lang } from '../../../types/core/lang.types';
interface Main {
  mainCounters: MonitoredCounter[] | null;
  mainCycleTime: MonitoredCycleTime | null;
  mainMonitoredState: MonitoredState | null;
}

/**
 * Performace dashboard
 * Zobrazuje aktuální a 2 předchozí směny.
 * Zobrazuje pouze main hodnoty a plán.
 */
export function DashboardPerformance() {
  const classes = useStyles();
  const { t, changeLang, lang } = useTranslation();
  const { parentId, name, id, lang: langParam, timezone: timezoneParam } = useParams();
  const {
    timezone,
    isOpen: isTimezoneDialogOpen,
    setOpen: setTimezoneDialogOpen,
    setTimezone,
  } = useTimezoneDialog();
  const assetId = Number(parentId);
  const isFullscreen = window.location.pathname.includes('fullscreen');

  useEffect(() => {
    if (!langParam) return;
    // @ts-ignore
    changeLang(Lang[langParam]);
  }, [langParam]);

  // Pokud je nastavená timezone přes parametr, nastavíme si state
  useEffect(() => {
    if (!timezoneParam) return;
    const _timezoneParam = timezoneParam.replace('-', '/');
    setTimezone({ value: _timezoneParam, label: _timezoneParam });
  }, [timezoneParam]);

  // Seznam counter dat, ve kterých budeme hledat main item
  const { data: counterData } = useQuery<MonitoredCounter[]>(['monitoredCounter'], () =>
    monitoredCounterApi.monitoredCounterGetList(assetId),
  );

  // Seznam cycle time dat, ve kterých budeme hledat main item
  const { data: cycleTimeData } = useQuery<MonitoredCycleTime[]>(['monitoredCycleTime'], () =>
    monitoredCycleTimeApi.monitoredCycleTimeGetList(assetId),
  );

  // Seznam monitored state dat, ve kterých budeme hledat main item
  const { data: monitoredStateData } = useQuery<MonitoredState[]>(['monitoredState'], () =>
    monitoredStateApi.monitoredStateGetList(assetId),
  );

  // Informace o assetu
  const { data: assetData, isLoading: assetIsLoading } = useQuery<Asset>(
    ['asset'],
    () => assetApi.getAsset(assetId),
    configNoCache,
  );

  // Otevře fullscreen verzi performance dashboardu pro aktuální asset
  const handleOpenFullScreen = () => {
    window.open(
      `${pathFullscreen.DASHBOARD}/${assetId}/${name && encodeURIComponent(name)}${
        paths.DASHBOARD_PERFORMANCE
      }/${Number(id)}/${lang}/${timezone.value.replace('/', '-')}`,
      'toolbar=no,resizable=yes,location=yes',
    );
  };

  // Otevírá okno pro výběr timezone
  const handleTimezoneOpen = () => {
    setTimezoneDialogOpen(true);
  };

  // Změna timezone
  const handleTimezoneChanged = (newTimezone: Timezone) => {
    setTimezone(newTimezone);
    setTimezoneDialogOpen(false);
  };

  // Načteme main hodnoty z načtených seznamů z API
  const main: Main | undefined = useMemo(() => {
    if (!counterData || !cycleTimeData || !monitoredStateData) return;

    const mainCounters = counterData.filter((x) => x.main);
    const mainCycleTime = cycleTimeData.find((x) => x.main);
    const mainMonitoredState = monitoredStateData.find((x) => x.main);

    return {
      mainCounters: mainCounters || null,
      mainCycleTime: mainCycleTime || null,
      mainMonitoredState: mainMonitoredState || null,
    };
  }, [counterData, cycleTimeData, monitoredStateData]);

  const returnEl = useMemo(() => {
    // Abychom mohli vykreslit dashboard, musíme mít načtené main hodnoty (NULLABLE)
    if (!main || assetIsLoading || !assetData) return <Loader />;

    const fullScreenButton = (
      <Tooltip title={t('general.action-buttons.fullscreen')}>
        <Button onClick={handleOpenFullScreen} variant={'contained'}>
          Fullscreen
        </Button>
      </Tooltip>
    );

    const timezoneButton = (
      <Tooltip title={t('general.timezone.tooltip-timezone')}>
        <Button
          onClick={handleTimezoneOpen}
          style={{
            fontSize: '0.6rem',
            top: (isFullscreen ? 3 : 12) + '%',
            marginRight: '0.6rem',
          }}
        >
          {timezone.label}
        </Button>
      </Tooltip>
    );

    let headerButtons = [timezoneButton];
    if (!isFullscreen) headerButtons = [...headerButtons, fullScreenButton];

    return (
      <>
        <PageHeader
          title={''}
          itemTitle={assetData.name}
          itemClassName={classes.itemTitle}
          xs={12}
          className={classes.header}
          headlineVariant={'h2'}
          buttons={headerButtons}
        />
        <DashboardDataLoader
          asset={assetData}
          mainCounters={main.mainCounters}
          mainCycleTime={main.mainCycleTime}
          mainMonitoredState={main.mainMonitoredState}
          timezone={timezone.value}
        />
      </>
    );
  }, [main, assetData, assetIsLoading, timezone, lang]);

  return (
    <>
      <Grid item xs={12} md={4}>
        <TimezoneChanger
          isOpen={isTimezoneDialogOpen}
          timezone={timezone}
          onChange={handleTimezoneChanged}
          onClose={() => setTimezoneDialogOpen(false)}
        />
      </Grid>
      {returnEl}
    </>
  );
}
